/**
 * Cases Library — hand-written types + TanStack Query hooks.
 *
 * The backend endpoints (`/cases`, `/cases/{id}`, `/cases/{id}/stage`) are
 * being built in parallel, so the generated SDK does not cover them yet.
 * These hooks go through the same configured @hey-api client (cookie auth,
 * `/api` proxy) as the generated SDK. Once the OpenAPI schema includes the
 * cases endpoints, swap the raw `client.get/patch` calls for the generated
 * functions and delete the hand-written types below.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { client } from "@/lib/api/client";
import { logger } from "@/lib/logger";

// ─── Types (mirror the backend contract) ─────────────────────────────────

export type CaseStage = "Draft" | "Approved";

export interface CaseSummary {
  id: string;
  client_name: string;
  slide_title: string;
  summary: string;
  industry: string[];
  service_lines: string[];
  region: string | null;
  country: string | null;
  year: number | null;
  stage: CaseStage;
  status: string;
  notion_page_url: string | null;
  slides_url: string | null;
  created_at: string;
  content_updated_at?: string | null;
  content_updated_by?: string | null;
}

export interface CaseJourneyPhase {
  phase: string | null;
  period: string | null;
  what_we_did: string | null;
  status: string | null;
}

export interface CaseContent {
  tagline: string | null;
  about_client: string | null;
  challenge: string | null;
  why_boi: string[] | null;
  journey: CaseJourneyPhase[] | null;
  results_commercial: string | null;
  results_speed: string | null;
  results_org: string | null;
  playbook: string | null;
  slide_sector: string | null;
  slide_title: string | null;
  slide_challenge: string | null;
  slide_strategy: string | null;
  slide_outcomes: string | null;
  slide_metric_1_value: string | null;
  slide_metric_1_label: string | null;
  slide_metric_2_value: string | null;
  slide_metric_2_label: string | null;
  image_prompt: string | null;
  project_team: string[] | null;
  sources: string[] | null;
  engagement_value: string | null;
  public_link: string | null;
}

export interface CaseDetail extends CaseSummary {
  /** Contractually `Record<string, unknown>`; the known keys are typed. */
  content: Partial<CaseContent>;
}

export interface CasesFilterParams {
  industry?: string;
  service_line?: string;
  region?: string;
  year?: string;
  stage?: string;
  q?: string;
}

// ─── Hooks ───────────────────────────────────────────────────────────────

/**
 * Fetches the full case list once; filtering is done client-side on the
 * library page (the server-side query params exist but the dataset is small).
 */
export function useCases() {
  return useQuery({
    queryKey: ["cases"],
    queryFn: async () => {
      const { data } = await client.get<CaseSummary[]>({ url: "/cases" });
      return data ?? [];
    },
  });
}

export function useCase(caseId: string | undefined) {
  return useQuery({
    queryKey: ["cases", caseId],
    queryFn: async () => {
      const { data } = await client.get<CaseDetail>({ url: `/cases/${caseId ?? ""}` });
      return data ?? null;
    },
    enabled: !!caseId,
  });
}

export function useUpdateCaseStage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { caseId: string; stage: CaseStage }) => {
      const { data } = await client.patch<CaseSummary>({
        url: `/cases/${vars.caseId}/stage`,
        body: { stage: vars.stage },
      });
      return data;
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({ queryKey: ["cases"] });
      void queryClient.invalidateQueries({ queryKey: ["cases", vars.caseId] });
    },
  });
}

/** Extracts a human-readable message from a FastAPI error body (e.g. 422 detail). */
function extractApiError(error: unknown, fallback: string): string {
  if (error && typeof error === "object" && "detail" in error) {
    const detail = (error as { detail: unknown }).detail;
    if (typeof detail === "string") {
      return detail;
    }
    if (Array.isArray(detail)) {
      const msgs = detail
        .map((d: unknown) =>
          d && typeof d === "object" && "msg" in d ? String((d as { msg: unknown }).msg) : null,
        )
        .filter((m): m is string => !!m);
      if (msgs.length > 0) {
        return msgs.join("; ");
      }
    }
  }
  return fallback;
}

/**
 * PATCH /cases/{id}/content — sends a partial CaseContent (only changed
 * fields); returns the updated CaseDetail. Throws with the server's
 * validation message on 422.
 */
export function useUpdateCaseContent(caseId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (changes: Partial<CaseContent>) => {
      logger.debug("[api]", "updating case content", { caseId, fields: Object.keys(changes) });
      const { data, error, response } = await client.patch<CaseDetail>({
        url: `/cases/${caseId ?? ""}/content`,
        body: changes,
      });
      if (error || !response.ok) {
        throw new Error(extractApiError(error, "Could not save changes."));
      }
      return data ?? null;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["cases"] });
    },
  });
}

export interface RegenerateSlideResult {
  ok: boolean;
  slides_url: string;
}

/** POST /cases/{id}/regenerate-slide — regenerates the slide deck. */
export function useRegenerateSlide(caseId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      logger.debug("[api]", "regenerating slide", { caseId });
      const { data, error, response } = await client.post<RegenerateSlideResult>({
        url: `/cases/${caseId ?? ""}/regenerate-slide`,
      });
      if (error || !response.ok || !data?.ok) {
        throw new Error(extractApiError(error, "Could not regenerate the slide."));
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["cases"] });
    },
  });
}
